import { isNil } from 'lodash'
import { WaterCoolingHeatPumpParameters } from '../../generated/graphql'
import { EnergyType } from '../EnergyFlow'
import {
  createHourlyProvided,
  createHourlyRequirement,
  createSecondaryHourlyWaste,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import { OutdoorTemperatureMissingError } from '../calculator-error'
import {
  SingleTypeHourlyVariables,
  SingleTypeProvider,
} from './common/SingleTypeProvider'

type NonFreeCoolingParameters = {
  freeCooling: false
  eer: number
}

type FreeCoolingParameters = Omit<NonFreeCoolingParameters, 'freeCooling'> & {
  freeCooling: true
  freeCoolingMaxOutdoorTemperature: number
  freeCoolingEer: number
}

/**
 * Converts electricity to cooling with an unlimited power.
 */
export class WaterCoolingHeatPumpProvider extends SingleTypeProvider<WaterCoolingHeatPumpParameters> {
  public readonly providedEnergyTypes = [EnergyType.Cooling]

  private validatedParameters!: FreeCoolingParameters | NonFreeCoolingParameters

  protected initialize(): void {
    const {
      eer,
      freeCooling,
      freeCoolingMaxOutdoorTemperature,
      freeCoolingEer,
    } = this.parameters

    if (!freeCooling) {
      this.validatedParameters = { eer, freeCooling }
      return
    }

    if (isNil(freeCoolingMaxOutdoorTemperature) || isNil(freeCoolingEer)) {
      throw new Error('Free cooling parameters must be defined')
    }

    this.validatedParameters = {
      eer,
      freeCooling,
      freeCoolingMaxOutdoorTemperature,
      freeCoolingEer,
    }
  }

  protected provideForHour({
    remainingDemand,
    outdoorTemperature,
  }: SingleTypeHourlyVariables): HourlyEnergyFlow[] {
    if (this.validatedParameters.freeCooling) {
      if (isNil(outdoorTemperature)) {
        throw new OutdoorTemperatureMissingError()
      }

      return this.provideFreeCooling(
        this.validatedParameters,
        remainingDemand,
        outdoorTemperature,
      )
    }

    return this.provideNonFreeCooling(this.validatedParameters, remainingDemand)
  }

  private provideFreeCooling(
    parameters: FreeCoolingParameters,
    remainingDemand: PrimaryHourlyEnergyFlow,
    outdoorTemperature: number,
  ): HourlyEnergyFlow[] {
    const { eer, freeCoolingMaxOutdoorTemperature, freeCoolingEer } = parameters

    const isFreeCoolingActive =
      outdoorTemperature < freeCoolingMaxOutdoorTemperature

    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      remainingDemand.value,
    )

    const requirement = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / (isFreeCoolingActive ? freeCoolingEer : eer),
    )

    const waste = createSecondaryHourlyWaste(
      provided.demandId,
      EnergyType.Heating,
      provided.value + requirement.value,
    )

    return [provided, waste, requirement]
  }

  private provideNonFreeCooling(
    parameters: NonFreeCoolingParameters,
    remainingDemand: PrimaryHourlyEnergyFlow,
  ): HourlyEnergyFlow[] {
    const { eer } = parameters

    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      remainingDemand.value,
    )

    const requirement = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / eer,
    )

    const waste = createSecondaryHourlyWaste(
      provided.demandId,
      EnergyType.Heating,
      provided.value + requirement.value,
    )

    return [provided, waste, requirement]
  }
}
